import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Anchor, Wine, Music, MapPin, Clock, Calendar, ChevronRight, Star, ArrowRight, Phone, Check, Flame, Crown, Shield, Users, GlassWater, Plus, Minus, Ticket } from "lucide-react";
import SEOHead from "../components/SEOHead";
import Starfield from "../components/Starfield";
import { MAGIC_PHOTOS, CONTACT } from "../data/cruiseData";
import EnquiryForm from "../components/EnquiryForm";
import NYECountdown from "../components/NYECountdown";
import useTicketPrices from "../hooks/useTicketPrices";

/* ────────────────────────────────────────────────────────────────────────────
 * NYE CRUISES SYDNEY HARBOUR, Premium luxury landing
 * Matte black + champagne gold + dark navy gradients · glass cards · elegant type
 * ──────────────────────────────────────────────────────────────────────────── */

const GOLD = "#D4AF37";
const CHAMPAGNE = "#F5E6C8";

const HIGHLIGHTS = [
 { icon: Sparkles, label: "9PM + MIDNIGHT", sub: "Two fireworks shows" },
 { icon: Anchor, label: "EXCLUSION ZONE", sub: "Private water access" },
 { icon: Wine, label: "ALL-INCLUSIVE", sub: "Premium open bar" },
 { icon: Music, label: "LIVE DJ", sub: "All night dance floor" }];

const TIMELINE = [
 { time: "7:30 PM", title: "Champagne boarding", body: "Step aboard at King St Wharf with a glass of Wolf Blass Cuvée on arrival." },
 { time: "8:30 PM", title: "Sunset cruise & canapés", body: "Vegetarian spring rolls, Thai fish cakes, sushi platters and gourmet party pies." },
 { time: "9:00 PM", title: "Family fireworks show", body: "Front-row to Sydney's 9pm fireworks from the Harbour Bridge exclusion zone." },
 { time: "10:00 PM", title: "Standing buffet & DJ", body: "Whole tiger prawns, BBQ Provencale chicken, artisan salads. Top deck opens up." },
 { time: "11:30 PM", title: "Bridge approach", body: "Cruise positions for the world's biggest midnight show, champagne refilled." },
 { time: "12:00 AM", title: "MIDNIGHT FIREWORKS", body: "12 minutes of pyrotechnics cascading off the Bridge. Best seat in the world." },
 { time: "1:30 AM", title: "Return to wharf", body: "DJ to the last call, return to King St Wharf at 1:30am." }];

const PACKAGES = [
 {
  tier: "EARLY BIRD",
  badge: "BUY TICKETS ONLINE",
  badgeColor: "bg-amber-300 text-[#0A0A0A]",
  adult: "$1,250",
  child: "$950",
  caption: "Per person · all-inclusive",
  inclusions: [
   "6 hours of cruising", "Welcome champagne on boarding", "Premium beverage package · Wolf Blass wines, Carlton Dry, Cuvée, fresh juice", "Canapés on arrival + standing buffet + dessert", "9pm + Midnight fireworks · Bridge exclusion zone", "Live DJ all night · dance floor open", "King St Wharf boarding · 7:30pm"],
  cta: "Buy NYE Tickets",
  ctaHref: "/cruises/nye-cruises-sydney-harbour/book",
  glow: "shadow-[0_30px_80px_-20px_rgba(212,175,55,0.45)] ring-amber-300/40",
  highlight: true,
 },
 {
  tier: "GROUP BOOKING",
  badge: "10+ GUESTS · ENQUIRE",
  badgeColor: "bg-white/15 text-white ring-1 ring-white/25",
  adult: "$1,250",
  child: "$950",
  caption: "Per person · 20% deposit option",
  inclusions: [
   "All Early Bird inclusions", "20% deposit to confirm (if 21+ days out)", "Final guest count due 7 days prior", "Full payment due 21 days prior", "Credit issued for any reductions up to 7 days prior", "Dedicated group coordinator", "Private group section on board"],
  cta: "Enquire about group booking",
  ctaHref: "#enquiry",
  glow: "shadow-[0_30px_80px_-20px_rgba(0,0,0,0.4)] ring-white/15",
 }];

const MENU_BLOCKS = [
 {
  id: "canapes",
  iconKey: "Sparkles",
  label: "Canapés on arrival",
  caption: "Served fresh as you board, paired with welcome Cuvée.",
  items: [
   "Vegetarian spring rolls (hot, veg)", "Spicy Thai fish cakes", "Assorted sushi platter (cold, veg, gf)", "Gourmet party pies, beef burgundy & chicken and leek (hot)"],
 },
 {
  id: "buffet",
  iconKey: "Wine",
  label: "Standing buffet",
  caption: "Premium spread laid out before the 9pm fireworks.",
  items: [
   "Whole tiger prawns served with lemon and Mary Rose mayo", "Oven roasted chicken breast, thyme infused BBQ Provencale", "Italian pasta salad with cherry tomato, kalamata, buffalo mozzarella, Italian dressing", "Classic potato salad, sour cream and chive with crispy streaky bacon", "Fresh garden salad with red capsicum, shredded carrot, Spanish onion, French dressing", "Selection of artisan bakery rolls with individual butter portions"],
 },
 {
  id: "dessert",
  iconKey: "Star",
  label: "Dessert table",
  caption: "Served while the boat positions for the midnight show.",
  items: ["Assorted cakes platter, plated to share"],
 },
 {
  id: "beverage",
  iconKey: "GlassWater",
  label: "Premium beverage package",
  caption: "Open bar across the full 6 hours of cruising.",
  items: [
   "Sparkling: Wolf Blass Eaglehawk Cuvée", "White: Wolf Blass Eaglehawk Sauvignon Blanc", "Red: Wolf Blass Eaglehawk Cabernet Sauvignon", "Beer: Carlton Dry, Great Northern Super Dry, Cascade Light", "Non alcoholic: Coca-Cola, soft drinks, fresh juice, still water"],
 }];

const ICON_MAP = { Sparkles, Wine, Star, GlassWater };


const REVIEWS = [
 { name: "Olivia M.", role: "NYE 2024 guest", rating: 5, body: "Best NYE we've ever had. Watching the midnight show from the water with champagne in hand. Nothing in Sydney beats Magic on NYE." },
 { name: "Marcus T.", role: "NYE 2024 guest", rating: 5, body: "Worth every cent. Front-row to two fireworks shows, premium food and drinks, DJ all night. We're booked in for 2026." },
 { name: "Sophie & Ben", role: "NYE 2024 guests", rating: 5, body: "We proposed on the upper deck just before midnight. The Magic crew popped champagne, the bridge lit up, the whole boat cheered. Unforgettable." },
 { name: "Daniel K.", role: "Corporate NYE 2024", rating: 5, body: "Took 20 clients out for NYE on Magic. Discreet service, unreal views and a finale none of us will forget. Already locked in 2026." }];

const FAQS = [
 { q: "When does NYE 2026 board and return?", a: "Boarding opens at 7:30pm at King St Wharf, Darling Harbour. The cruise returns approximately 1:30am after the midnight fireworks." },
 { q: "What's included in the ticket price?", a: "Adult $1,250 / Child $950 includes: champagne on arrival, full beverage package (Wolf Blass Eaglehawk Sauvignon Blanc, Cabernet Sauvignon, Cuvée sparkling; Carlton Dry, Great Northern Super Dry, Cascade Light beers; Coca-Cola, soft drinks and fresh juice), canapés on arrival, standing buffet (whole tiger prawns, BBQ Provencale chicken, Italian pasta salad, potato salad, garden salad, artisan rolls), dessert cakes, live DJ and front-row exclusion zone access for both the 9pm and midnight fireworks." },
 { q: "Can we see the Bridge fireworks from the boat?", a: "Yes. Magic Cruises operates inside the official Harbour Bridge exclusion zone, which means you get an unobstructed front-row view of both the 9pm family fireworks and the 12 minute midnight Bridge Effect." },
 { q: "Is there a dress code?", a: "Smart cocktail attire. Many Magic guests dress to the nines for NYE. Heels are welcome and flats are encouraged for the dance floor." },
 { q: "What's your booking and refund policy?", a: "Individual bookings (less than 10 guests): full payment at time of booking, free cancellation up to 72 hours prior. Group bookings (10+): 20% deposit option if more than 21 days from cruise date. Final guest count must be confirmed 7 days prior; full payment due 21 days prior. Credit issued for any reductions up to 7 days prior." },
 { q: "Are kids welcome?", a: "Absolutely. NYE on Magic is family-friendly. The 9pm fireworks are perfect for younger guests. Many families head home after, while others stay the full course through midnight." }];

/* ───────── Sub-components ───────── */
function GoldEyebrow({ children }) {
 return (
  <div className="inline-flex items-center gap-3 text-[10px] uppercase tracking-[0.4em] font-black" style={{ color: GOLD }}>
   <span className="w-8 h-px" style={{ backgroundColor: GOLD, opacity: 0.6 }} />
   {children}
  </div>
 );
}

function MenuAccordion({ block, defaultOpen, i }) {
 const [open, setOpen] = useState(!!defaultOpen);
 const Icon = ICON_MAP[block.iconKey] || Sparkles;
 return (
  <div className={`relative rounded-2xl backdrop-blur transition-all ${open ? "bg-gradient-to-br from-amber-300/[0.08] via-white/[0.03] to-transparent ring-1 ring-amber-300/30" : "bg-white/[0.04] ring-1 ring-white/10 hover:ring-white/25"}`} data-testid={`nye-menu-${block.id}`}>
   <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-4 lg:gap-5 px-5 lg:px-7 py-5 lg:py-6 text-left">
    <span className="shrink-0 w-12 h-12 rounded-full flex items-center justify-center ring-1" style={{ backgroundColor: open ? GOLD : "rgba(212,175,55,0.10)", color: open ? "#0A0A0A" : GOLD, borderColor: GOLD }}>
     <Icon className="w-5 h-5" strokeWidth={open ? 2.5 : 1.5} fill={open && block.iconKey === "Star" ? "#0A0A0A" : "none"} />
    </span>
    <div className="flex-1 min-w-0">
     <div className="text-[10px] uppercase tracking-[0.32em] font-black" style={{ color: GOLD }}>Course 0{i + 1}</div>
     <div className="font-black text-white text-lg lg:text-xl tracking-tight leading-tight mt-1">{block.label}</div>
     <div className="text-white/55 text-[13px] mt-1 font-light hidden sm:block">{block.caption}</div>
    </div>
    <span className="shrink-0 w-8 h-8 rounded-full ring-1 ring-white/20 flex items-center justify-center text-white/70 transition-all" style={open ? { borderColor: GOLD, color: GOLD } : {}}>
     {open ? <Minus className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
    </span>
   </button>
   {open && (
    <div className="px-5 lg:px-7 pb-6 lg:pb-7 pl-[4.5rem] lg:pl-[5.75rem] animate-in fade-in slide-in-from-top-1 duration-200">
     <ul className="space-y-2.5 border-l border-amber-300/30 pl-5">
      {block.items.map((it, j) => (
       <li key={j} className="text-white/80 text-sm lg:text-[15px] leading-relaxed flex items-start gap-3">
        <span className="shrink-0 mt-2 w-1 h-1 rounded-full" style={{ backgroundColor: GOLD }} />
        <span>{it}</span>
       </li>
      ))}
     </ul>
    </div>
   )}
  </div>
 );
}

function FaqItem({ q, a, i }) {
 const [open, setOpen] = useState(false);
 return (
  <div className="border-b border-white/8 py-5" data-testid={`nye-faq-${i}`}>
   <button onClick={() => setOpen(!open)} className="w-full flex items-start justify-between gap-6 text-left group">
    <span className="font-bold text-white text-base lg:text-lg tracking-tight group-hover:text-amber-300 transition-colors">{q}</span>
    <span className="shrink-0 mt-1 w-7 h-7 rounded-full ring-1 ring-white/20 flex items-center justify-center text-white/70 group-hover:text-amber-300 group-hover:ring-amber-300/50 transition-all">
     {open ? <Minus className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
    </span>
   </button>
   {open && <p className="text-white/65 text-sm lg:text-[15px] leading-relaxed mt-3 pr-12 animate-in fade-in slide-in-from-top-1 duration-200">{a}</p>}
  </div>
 );
}

/* ───────── PAGE ───────── */
export default function NYECruise() {
 const [showStickyCTA, setShowStickyCTA] = useState(false);
 const { priceFor } = useTicketPrices({ "nye": { adult: 1250, child: 950 } });
 const adultStr = priceFor("nye", "adult") || "$1,250";
 const childStr = priceFor("nye", "child") || "$950";
 const livePackages = useMemo(
  () => PACKAGES.map((p) => ({ ...p, adult: adultStr, child: childStr })),
  [adultStr, childStr],
 );

 useEffect(() => {
  const onScroll = () => setShowStickyCTA(window.scrollY > 800);
  window.addEventListener("scroll", onScroll, { passive: true });
  return () => window.removeEventListener("scroll", onScroll);
 }, []);

 return (
  <main className="bg-[#0A0A0A] text-white overflow-hidden" data-testid="nye-page">
   <SEOHead
    title="Sydney Harbour NYE Cruise 2026 · Front-Row Fireworks | Magic Cruises"
    description="Sydney's most premium NYE cruise. 6 hours of cruising, front-row exclusion zone access to the 9pm + midnight fireworks. All inclusive: champagne, premium drinks, canapés, buffet, live DJ. Adult $1,250 / Child $950. Boarding 7:30pm King St Wharf."
    image="https://customer-assets.emergentagent.com/job_magic-harbour-party/artifacts/srsraacs_sydney%20cruises%20boats.png"
    canonical="/cruises/nye-cruises-sydney-harbour"
    keywords="NYE cruise Sydney Harbour, New Year's Eve cruise Sydney, NYE 2026 Sydney tickets, Sydney fireworks cruise, midnight fireworks cruise Sydney, exclusion zone NYE cruise"
    includeLocalBusiness
    breadcrumbs={[
     { name: "Home", url: "/" },
     { name: "Ticketed Cruises", url: "/ticketed-cruises" },
     { name: "NYE Cruises Sydney Harbour", url: "/cruises/nye-cruises-sydney-harbour" },
    ]}
    eventSchema={{
     name: "Sydney Harbour NYE 2026 Fireworks Cruise · MV Sydney Magic",
     startDate: "2026-12-31T19:30:00+11:00",
     endDate: "2027-01-01T01:30:00+11:00",
     description: "6 hours on Sydney Harbour with front-row exclusion zone access to the 9pm and midnight fireworks, premium beverage package, canapés, standing buffet and live DJ. Boarding 7:30pm King St Wharf 3.",
     offers: { price: 1250, availability: "https://schema.org/InStock" },
    }}
   />

   {/* ═══════════════════════════════════════════════════════════════════════════
   HERO · cinematic NYE poster · real glitter texture + flickering stars
   ═══════════════════════════════════════════════════════════════════════════ */}
   <section className="relative overflow-hidden bg-[#06060A] flex flex-col isolate" data-testid="nye-hero">
    <div className="relative w-full min-h-[92vh] lg:min-h-[96vh] flex items-center justify-center px-5 sm:px-8 lg:px-10 pt-28 pb-16 lg:py-28">
     {/* Layer 1 · soft photo backdrop blown out so it reads like a painted backdrop */}
     <img
      src="https://customer-assets.emergentagent.com/job_magic-harbour-party/artifacts/pitzfhxp_sydney%20harbour%20nye%20cruise.png"
      alt=""
      aria-hidden="true, "
      className="absolute inset-0 w-full h-full object-cover opacity-55"
      style={{ objectPosition: "95% 50%", transform: "scale(1.6)", transformOrigin: "right center" }}
      fetchpriority="high"
     />

     {/* Layer 2 · deep midnight gradient wash · gives the photo a navy/black depth */}
     <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(6,6,16,0.85) 0%, rgba(15,10,40,0.55) 28%, rgba(8,4,22,0.55) 60%, rgba(4,2,12,0.92) 100%)" }} />

     {/* Layer 3 · soft warm amber light leak at the top + magenta at the bottom */}
     <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[120%] h-[55%] opacity-70" style={{ background: "radial-gradient(ellipse at center top, rgba(255,200,90,0.28) 0%, transparent 60%)" }} />
     <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[120%] h-[60%] opacity-70" style={{ background: "radial-gradient(ellipse at center bottom, rgba(218,80,180,0.25) 0%, transparent 65%)" }} />

     {/* Layer 4 · subtle film-grain texture */}
     <div className="absolute inset-0 opacity-[0.18] mix-blend-overlay pointer-events-none" style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='220' height='220'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.35 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")" }} />

     {/* Layer 5 · flickering starfield · the magic */}
     <Starfield density={90} color="#FFE9A8" glow="rgba(255,210,120,0.9)" fixed={false} />

     {/* Layer 6 · big gold orbs and a magenta orb */}
     <div className="absolute top-[12%] right-[8%] w-[28rem] h-[28rem] rounded-full bg-amber-400/30 blur-[120px] pointer-events-none animate-pulse" style={{ animationDuration: "6s" }} />
     <div className="absolute bottom-[8%] left-[6%] w-[22rem] h-[22rem] rounded-full bg-fuchsia-500/25 blur-[100px] pointer-events-none animate-pulse" style={{ animationDuration: "9s", animationDelay: "2s" }} />

     {/* SEO H1 */}
     <h1 className="sr-only">Sydney Harbour NYE 2026 Cruise aboard Magic, front row fireworks, premium drinks and an unforgettable night.</h1>

     {/* Top-right floating badges · desktop */}
     <div className="absolute top-24 lg:top-28 right-5 lg:right-10 z-20 hidden lg:flex flex-col gap-2 items-end">
      {["Limited Tickets", "Exclusion Zone", "All Inclusive", "Aboard Magic"].map((b, i) => (
       <div key={b} className="px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-xl ring-1 ring-amber-300/40 text-white text-[10px] uppercase tracking-[0.28em] font-black animate-in slide-in-from-right fade-in duration-700" style={{ animationDelay: `${i * 120}ms` }}>
        <span className="inline-flex items-center gap-2">
         <span className="w-1 h-1 rounded-full" style={{ backgroundColor: GOLD }} />
         {b}
        </span>
       </div>
      ))}
     </div>

     {/* CENTRE COMPOSITION */}
     <div className="relative z-10 text-center max-w-5xl mx-auto w-full">
      {/* Top eyebrow with gold rule */}
      <div className="inline-flex items-center gap-3 sm:gap-4 mb-5 lg:mb-7 animate-in fade-in slide-in-from-top-4 duration-1000">
       <span className="block w-10 sm:w-14 h-px" style={{ backgroundColor: GOLD, opacity: 0.7 }} />
       <span className="text-white/95 text-[11px] sm:text-[13px] lg:text-sm uppercase tracking-[0.5em] font-bold">Sydney Harbour</span>
       <span className="block w-10 sm:w-14 h-px" style={{ backgroundColor: GOLD, opacity: 0.7 }} />
      </div>

      {/* NYE 2026 · GLITTER WRITING */}
      <div className="relative inline-block animate-in fade-in zoom-in-95 duration-1000">
       <span
        className="nye-glitter font-black inline-block leading-[0.82]"
        style={{
         fontFamily: "'Cinzel Decorative''Playfair Display', serif",
         fontSize: "clamp(4.5rem, 17vw, 13rem)",
         letterSpacing: "0.01em",
         WebkitTextStroke: "1px rgba(255,232,160,0.22)",
        }}
       >
        NYE 2026
       </span>
       {/* Big sparkle stars on the corners */}
       <svg viewBox="0 0 24 24" className="absolute -top-4 -right-3 lg:-top-7 lg:-right-7 w-8 h-8 lg:w-12 lg:h-12 nye-spark-a" style={{ filter: "drop-shadow(0 0 12px rgba(255,222,130,0.9)) drop-shadow(0 0 22px rgba(255,200,80,0.7))" }}>
        <path d="M12 0 L14 10 L24 12 L14 14 L12 24 L10 14 L0 12 L10 10 Z" fill={CHAMPAGNE} />
       </svg>
       <svg viewBox="0 0 24 24" className="absolute -bottom-2 -left-3 lg:-bottom-4 lg:-left-7 w-6 h-6 lg:w-9 lg:h-9 nye-spark-b" style={{ filter: "drop-shadow(0 0 10px rgba(255,210,110,0.9)) drop-shadow(0 0 18px rgba(255,180,60,0.6))" }}>
        <path d="M12 0 L14 10 L24 12 L14 14 L12 24 L10 14 L0 12 L10 10 Z" fill={GOLD} />
       </svg>
       <svg viewBox="0 0 24 24" className="absolute top-1/2 -right-10 lg:-right-16 w-4 h-4 lg:w-6 lg:h-6 nye-spark-c hidden sm:block" style={{ filter: "drop-shadow(0 0 8px rgba(255,232,160,0.9))" }}>
        <path d="M12 0 L14 10 L24 12 L14 14 L12 24 L10 14 L0 12 L10 10 Z" fill="#FFF1B3" />
       </svg>
       <svg viewBox="0 0 24 24" className="absolute top-1/3 -left-10 lg:-left-16 w-4 h-4 lg:w-6 lg:h-6 nye-spark-d hidden sm:block" style={{ filter: "drop-shadow(0 0 8px rgba(255,232,160,0.9))" }}>
        <path d="M12 0 L14 10 L24 12 L14 14 L12 24 L10 14 L0 12 L10 10 Z" fill="#FFF1B3" />
       </svg>
      </div>

      {/* "Cruise", glitter handwriting */}
      <div className="-mt-3 sm:-mt-6 lg:-mt-10 relative">
       <span
        className="nye-glitter-script inline-block"
        style={{
         fontFamily: "'Tangerine', 'Great Vibes', cursive",
         fontWeight: 700,
         fontSize: "clamp(4.5rem, 14vw, 10rem)",
         lineHeight: 1.15,
         letterSpacing: "0",
         transform: "rotate(-4deg)",
         paddingBottom: "0.1em",
        }}
       >
        Cruise
       </span>
       <svg viewBox="0 0 24 24" className="absolute -top-3 right-1/3 w-5 h-5 lg:w-7 lg:h-7 nye-spark-c" style={{ filter: "drop-shadow(0 0 10px rgba(255,222,130,0.9))" }}>
        <path d="M12 0 L14 10 L24 12 L14 14 L12 24 L10 14 L0 12 L10 10 Z" fill={CHAMPAGNE} />
       </svg>
      </div>

      {/* Tagline strip */}
      <div className="mt-5 lg:mt-7 flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 text-white text-[11px] sm:text-sm lg:text-base font-bold tracking-[0.18em] uppercase animate-in fade-in slide-in-from-bottom-4 duration-1000" style={{ animationDelay: "300ms" }}>
       <span>Front Row Fireworks</span>
       <span style={{ color: GOLD }}>✦</span>
       <span>Premium Open Bar</span>
       <span style={{ color: GOLD }}>✦</span>
       <span>Unforgettable Night</span>
      </div>

      {/* ABOARD MAGIC */}
      <div className="mt-7 lg:mt-9 inline-flex items-center gap-3 lg:gap-4 animate-in fade-in duration-1000" style={{ animationDelay: "500ms" }}>
       <span className="text-white/90 text-[11px] sm:text-sm lg:text-base uppercase tracking-[0.4em] font-bold">Aboard</span>
       <span
        className="nye-glitter-script inline-block"
        style={{
         fontFamily: "'Tangerine', 'Great Vibes', cursive",
         fontWeight: 700,
         fontSize: "clamp(2.75rem, 6vw, 4.5rem)",
         lineHeight: 1.3,
         paddingBottom: "0.15em",
        }}
       >
        Magic
       </span>
      </div>

      {/* LIVE COUNTDOWN to NYE 2026 boarding */}
      <div className="mt-10 lg:mt-12 flex justify-center animate-in fade-in slide-in-from-bottom-4 duration-1000" style={{ animationDelay: "600ms" }}>
       <NYECountdown gold={GOLD} champagne={CHAMPAGNE} testid="nye-hero-countdown" />
      </div>

      {/* Bottom icon callouts */}
      <div className="mt-12 lg:mt-16 grid grid-cols-3 gap-4 lg:gap-10 max-w-3xl mx-auto animate-in fade-in duration-1000" style={{ animationDelay: "700ms" }}>
       {[
        { i: MapPin, label: ["Exclusion", "Zone Access"] },
        { i: Clock, label: ["6.5 Hour", "All Inclusive"] },
        { i: Music, label: ["Live DJ &", "Entertainment"] }].map((c, i) => (
        <div key={i} className="flex flex-col items-center gap-2 lg:gap-3 text-center">
         <span className="w-11 h-11 lg:w-14 lg:h-14 rounded-full flex items-center justify-center ring-1 backdrop-blur" style={{ backgroundColor: "rgba(212,175,55,0.10)", borderColor: GOLD }}>
          <c.i className="w-4 h-4 lg:w-6 lg:h-6" style={{ color: GOLD }} strokeWidth={1.5} />
         </span>
         <div className="text-white text-[10px] sm:text-[11px] lg:text-sm uppercase tracking-[0.18em] lg:tracking-[0.22em] font-black leading-snug">
          {c.label.map((line, j) => <div key={j}>{line}</div>)}
         </div>
        </div>
       ))}
      </div>
     </div>
    </div>

    {/* CTA BAR · sits below the hero on its own dark band */}
    <div className="relative z-10 bg-[#06060A] border-t border-amber-300/25 px-4 lg:px-10 py-5 lg:py-7">
     <div className="mx-auto max-w-7xl">
      <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-8">
       <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <div className="text-amber-300 text-[10px] uppercase tracking-[0.32em] font-black inline-flex items-center gap-3">
         <span className="w-6 h-px" style={{ backgroundColor: GOLD, opacity: 0.6 }} />
         31 Dec 2026 · 7:30pm
        </div>
        <div className="flex items-center gap-3 lg:gap-5 text-white text-xs lg:text-sm">
         <span className="inline-flex items-center gap-1.5 font-black"><Crown className="w-3.5 h-3.5 lg:w-4 lg:h-4" style={{ color: GOLD }} /> Adult <span style={{ color: GOLD }}>{adultStr}</span></span>
         <span className="text-white/30">·</span>
         <span className="inline-flex items-center gap-1.5 font-black"><Users className="w-3.5 h-3.5 lg:w-4 lg:h-4" style={{ color: GOLD }} /> Child <span style={{ color: GOLD }}>{childStr}</span></span>
        </div>
       </div>
       <div className="flex flex-wrap items-center gap-3 lg:ml-auto">
        <Link to="/cruises/nye-cruises-sydney-harbour/book" className="group flex-1 lg:flex-initial inline-flex items-center justify-center gap-2.5 px-6 lg:px-7 py-3.5 rounded-full font-black text-xs lg:text-sm tracking-[0.18em] uppercase shadow-[0_15px_40px_-10px_rgba(212,175,55,0.55)] hover:scale-[1.03] transition-all text-[#0A0A0A]" style={{ background: `linear-gradient(135deg, ${CHAMPAGNE} 0%, ${GOLD} 100%)` }} data-testid="nye-hero-book">
         Buy Tickets <ArrowRight className="w-4 h-4" />
        </Link>
        <a href="#packages" className="group flex-1 lg:flex-initial inline-flex items-center justify-center gap-2.5 px-5 lg:px-6 py-3.5 rounded-full font-black text-xs lg:text-sm tracking-[0.18em] uppercase border border-white/30 hover:border-amber-300/60 hover:text-amber-300 transition-all text-white" data-testid="nye-hero-packages">
         Packages <ChevronRight className="w-4 h-4" />
        </a>
       </div>
      </div>
     </div>
    </div>

    {/* NYE GLITTER + sparkle keyframes */}
    <style>{`
     /* Glitter text · layered radial-gradient micro-dots over a moving gold gradient · all clipped to text */
     .nye-glitter {
      background-color: #D4AF37;
      background-image:
        radial-gradient(circle at 12% 22%, rgba(255,255,255,0.95) 0.6px, transparent 1.4px),
        radial-gradient(circle at 78% 18%, rgba(255,247,200,0.95) 0.5px, transparent 1.2px),
        radial-gradient(circle at 34% 72%, rgba(255,255,255,0.9) 0.6px, transparent 1.4px),
        radial-gradient(circle at 88% 64%, rgba(255,240,170,0.95) 0.5px, transparent 1.2px),
        radial-gradient(circle at 18% 88%, rgba(255,255,255,0.85) 0.55px, transparent 1.3px),
        radial-gradient(circle at 56% 46%, rgba(255,250,210,0.95) 0.5px, transparent 1.2px),
        radial-gradient(circle at 68% 92%, rgba(255,255,255,0.85) 0.5px, transparent 1.2px),
        linear-gradient(120deg, #F8E9B7 0%, #F5C75A 12%, #D4AF37 28%, #FFF6C8 42%, #D4AF37 58%, #B8860B 72%, #F5E6C8 88%, #FFE9A8 100%);
      background-size: 14px 14px, 18px 18px, 11px 11px, 16px 16px, 13px 13px, 17px 17px, 12px 12px, 240% 240%;
      background-position: 0 0, 0 0, 0 0, 0 0, 0 0, 0 0, 0 0, 0% 50%;
      -webkit-background-clip: text;
      background-clip: text;
      color: transparent;
      -webkit-text-fill-color: transparent;
      animation: nye-glitter-shift 6s linear infinite, nye-glitter-shine 5s ease-in-out infinite;
     }
     .nye-glitter-script {
      background-color: #F5E6C8;
      background-image:
        radial-gradient(circle at 22% 28%, rgba(255,255,255,0.95) 0.7px, transparent 1.5px),
        radial-gradient(circle at 70% 18%, rgba(255,255,240,0.95) 0.6px, transparent 1.3px),
        radial-gradient(circle at 40% 70%, rgba(255,250,210,0.95) 0.6px, transparent 1.3px),
        radial-gradient(circle at 86% 78%, rgba(255,255,255,0.9) 0.65px, transparent 1.4px),
        linear-gradient(125deg, #FFFFFF 0%, #FFF6C8 22%, #F8DD7A 45%, #D4AF37 68%, #FFE9A8 90%);
      background-size: 16px 16px, 13px 13px, 18px 18px, 12px 12px, 220% 220%;
      background-position: 0 0, 0 0, 0 0, 0 0, 0% 50%;
      -webkit-background-clip: text;
      background-clip: text;
      color: transparent;
      -webkit-text-fill-color: transparent;
      animation: nye-glitter-shift 7s linear infinite, nye-glitter-shine 5s ease-in-out infinite;
     }
     @keyframes nye-glitter-shift {
      0% { background-position: 0 0, 0 0, 0 0, 0 0, 0 0, 0 0, 0 0, 0% 50%; }
      100% { background-position: 60px 30px, -50px 40px, 40px -50px, -30px -60px, 50px 50px, -40px 30px, 20px -30px, 100% 50%; }
     }
     @keyframes nye-glitter-shine {
      0%, 100% { filter: drop-shadow(0 2px 0 rgba(120,80,10,0.45)) drop-shadow(0 0 22px rgba(255,210,110,0.55)) drop-shadow(0 0 50px rgba(255,180,60,0.35)); }
      50% { filter: drop-shadow(0 2px 0 rgba(120,80,10,0.45)) drop-shadow(0 0 30px rgba(255,232,160,0.85)) drop-shadow(0 0 70px rgba(255,200,80,0.55)); }
     }
     /* Sparkle pulse + rotate animations for the SVG stars */
     @keyframes nye-spark { 0%,100% { transform: rotate(0deg) scale(0.85); opacity: 0.55; } 50% { transform: rotate(180deg) scale(1.2); opacity: 1; } }
     .nye-spark-a { animation: nye-spark 4.2s ease-in-out 0s infinite; }
     .nye-spark-b { animation: nye-spark 3.4s ease-in-out 0.6s infinite; }
     .nye-spark-c { animation: nye-spark 5.1s ease-in-out 1.3s infinite; }
     .nye-spark-d { animation: nye-spark 4.6s ease-in-out 2.1s infinite; }
    `}</style>
   </section>

   {/* ═══════════════════════════════════════════════════════════════════════════
    HIGHLIGHTS STRIP · navy band that separates hero from next section
    ═══════════════════════════════════════════════════════════════════════════ */}
   <section className="relative border-y border-amber-300/15 bg-gradient-to-r from-[#100926] via-[#0B0820] to-[#100926]" data-testid="nye-highlights">
    <div className="absolute inset-0 opacity-50 pointer-events-none" style={{ background: "radial-gradient(ellipse at 20% 50%, rgba(212,175,55,0.10) 0%, transparent 60%), radial-gradient(ellipse at 80% 50%, rgba(218,80,180,0.08) 0%, transparent 60%)" }} />
    <div className="relative mx-auto max-w-7xl px-5 lg:px-10 py-10 lg:py-14">
     <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-10 lg:gap-x-10">
      {HIGHLIGHTS.map((h, i) => (
       <div key={i} className="flex items-center gap-4 lg:border-r lg:border-amber-300/15 lg:pr-6 last:border-0">
        <div className="w-14 h-14 rounded-full flex items-center justify-center shrink-0 ring-1" style={{ backgroundColor: "rgba(212,175,55,0.10)", borderColor: GOLD }}>
         <h.icon className="w-6 h-6" style={{ color: GOLD }} strokeWidth={1.5} />
        </div>
        <div>
         <div className="font-black text-white text-sm tracking-[0.18em]">{h.label}</div>
         <div className="text-white/60 text-[11px] uppercase tracking-[0.22em] font-bold mt-1">{h.sub}</div>
        </div>
       </div>
      ))}
     </div>
    </div>
   </section>

   {/* ═══════════════════════════════════════════════════════════════════════════
    EXPERIENCE OVERVIEW · deep midnight indigo with starfield
    ═══════════════════════════════════════════════════════════════════════════ */}
   <section className="relative bg-gradient-to-b from-[#0A0618] via-[#0E0820] to-[#0A0618] py-24 lg:py-32 overflow-hidden isolate" data-testid="nye-experience">
    <Starfield density={45} color="#FFE9A8" glow="rgba(255,210,120,0.6)" fixed={false} intensity={0.7} />
    <div className="absolute -top-32 left-1/3 w-[40rem] h-[40rem] rounded-full bg-amber-400/[0.08] blur-3xl pointer-events-none" />
    <div className="absolute -bottom-32 right-1/4 w-[34rem] h-[34rem] rounded-full bg-fuchsia-500/[0.08] blur-3xl pointer-events-none" />
    <div className="relative z-10 mx-auto max-w-4xl px-5 lg:px-10 text-center">
     <div className="flex justify-center"><GoldEyebrow>The experience</GoldEyebrow></div>
     <h2 className="font-black tracking-tight text-white mt-6 leading-[0.92]" style={{ fontSize: "clamp(2.25rem, 5vw, 4.5rem)", letterSpacing: "-0.025em" }}>
      Not a cruise.<br /><em className="font-light" style={{ color: GOLD }}>An audience with Sydney.</em>
     </h2>
     <p className="text-white/75 text-lg lg:text-xl leading-[1.75] mt-8 font-light max-w-3xl mx-auto">
      Sydney's NYE doesn't belong on a footpath. It belongs on the water, on Magic, inside the official Harbour Bridge exclusion zone with a glass of champagne in hand and the world's most photographed bridge cascading 12 minutes of fireworks directly above you.
     </p>
     <p className="text-white/60 text-base lg:text-lg leading-[1.75] mt-6 font-light max-w-3xl mx-auto">
      Six hours on Magic. Two fireworks shows. Premium open bar. A DJ that doesn't stop. Views no land venue in Sydney can match.
     </p>

     {/* 6-up icon grid centred under the copy */}
     <div className="mt-12 grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-5 max-w-3xl mx-auto">
      {[
       { i: Sparkles, t: "Front-row fireworks" },
       { i: GlassWater, t: "Premium open bar" },
       { i: Music, t: "DJ all night" },
       { i: Wine, t: "Canapés + buffet" },
       { i: Anchor, t: "Exclusion zone access" },
       { i: Crown, t: "VIP boarding" }].map((x, i) => (
       <div key={i} className="flex items-center gap-3 text-white/90 text-sm lg:text-[15px] justify-center lg:justify-start">
        <span className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 ring-1" style={{ backgroundColor: "rgba(212,175,55,0.10)", borderColor: GOLD }}>
         <x.i className="w-4 h-4" style={{ color: GOLD }} />
        </span>
        <span className="font-semibold tracking-tight">{x.t}</span>
       </div>
      ))}
     </div>
    </div>
   </section>

   {/* ═══════════════════════════════════════════════════════════════════════════
    PACKAGES · warm dark amber-tinted backdrop, separates from indigo above
    ═══════════════════════════════════════════════════════════════════════════ */}
   <section id="packages" className="relative bg-gradient-to-b from-[#140C08] via-[#1A1108] to-[#100A06] py-24 lg:py-32 overflow-hidden isolate border-y border-amber-300/10" data-testid="nye-packages">
    <Starfield density={35} color="#FFE9A8" glow="rgba(255,210,120,0.5)" fixed={false} intensity={0.55} />
    <div className="absolute -top-32 right-1/4 w-[36rem] h-[36rem] rounded-full bg-amber-400/[0.12] blur-3xl pointer-events-none" />
    <div className="absolute -bottom-32 left-1/4 w-[32rem] h-[32rem] rounded-full bg-fuchsia-500/[0.08] blur-3xl pointer-events-none" />
    <div className="relative z-10 mx-auto max-w-7xl px-5 lg:px-10">
     <div className="text-center max-w-3xl mx-auto mb-14">
      <div className="flex justify-center"><GoldEyebrow>Tickets · NYE 2026</GoldEyebrow></div>
      <h2 className="font-black tracking-tight text-white mt-6 leading-[0.92]" style={{ fontSize: "clamp(2rem, 5vw, 4.5rem)", letterSpacing: "-0.025em" }}>
       Lock in your <em className="font-light" style={{ color: GOLD }}>VIP seat.</em>
      </h2>
      <p className="text-white/65 text-base lg:text-lg leading-[1.75] mt-5 font-light">
       NYE 2026 (31 December) is selling fast , secure your seat to welcome 2027 from Sydney Harbour. Two booking pathways below.
      </p>
     </div>

     <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
      {livePackages.map((p, i) => (
       <div key={i} className={`relative rounded-3xl ring-1 ${p.glow} backdrop-blur-xl p-8 lg:p-10 group hover:-translate-y-1 transition-all duration-500 overflow-hidden ${p.highlight ? "bg-gradient-to-br from-[#1a1409] via-[#0E0E10] to-[#0A0A0A]" : "bg-[#111113]"}`} data-testid={`nye-package-${i}`}>
        {p.highlight && (
         <div className="absolute inset-0 pointer-events-none opacity-60 group-hover:opacity-100 transition-opacity">
          <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full blur-3xl" style={{ backgroundColor: "rgba(212,175,55,0.15)" }} />
          <div className="absolute -bottom-20 -left-20 w-48 h-48 rounded-full bg-fuchsia-500/10 blur-3xl" />
         </div>
        )}
        <div className="relative">
         <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.28em] font-black ${p.badgeColor}`}>{p.badge}</div>
         <div className="text-[11px] uppercase tracking-[0.4em] font-black mt-5" style={{ color: GOLD }}>{p.tier}</div>
         <div className="flex items-baseline gap-3 mt-3">
          <span className="font-black text-white tracking-tight leading-none" style={{ fontSize: "clamp(2.75rem, 5vw, 4.25rem)", letterSpacing: "-0.03em" }}>{p.adult}</span>
          <span className="text-white/45 font-bold text-sm">adult</span>
         </div>
         <div className="text-white/55 text-sm mt-1">+ <span className="text-white font-bold">{p.child}</span> per child · {p.caption}</div>

         <ul className="mt-7 space-y-3">
          {p.inclusions.map((inc, j) => (
           <li key={j} className="flex items-start gap-3 text-white/85 text-sm leading-relaxed">
            <span className="shrink-0 mt-0.5 w-4 h-4 rounded-full flex items-center justify-center" style={{ backgroundColor: p.highlight ? GOLD : "rgba(255,255,255,0.1)" }}>
             <Check className={`w-2.5 h-2.5 ${p.highlight ? "text-[#0A0A0A]" : "text-white"}`} strokeWidth={3} />
            </span>
            <span>{inc}</span>
           </li>
          ))}
         </ul>

         {p.ctaHref?.startsWith("/") ? (
          <Link to={p.ctaHref} className={`mt-8 group/cta flex items-center justify-center gap-2.5 w-full px-6 py-4 rounded-full font-black text-sm tracking-[0.18em] uppercase transition-all ${p.highlight ? "text-[#0A0A0A] shadow-[0_15px_40px_-10px_rgba(212,175,55,0.6)] hover:shadow-[0_20px_50px_-10px_rgba(212,175,55,0.8)]" : "text-white ring-1 ring-white/25 hover:ring-amber-300/50 hover:text-amber-300"}`} style={p.highlight ? { background: `linear-gradient(135deg, ${CHAMPAGNE} 0%, ${GOLD} 100%)` } : {}} data-testid={`nye-package-cta-${i}`}>
           {p.cta} <ArrowRight className="w-4 h-4 transition-transform group-hover/cta:translate-x-0.5" />
          </Link>
         ) : (
          <a href={p.ctaHref || "#enquiry"} className={`mt-8 group/cta flex items-center justify-center gap-2.5 w-full px-6 py-4 rounded-full font-black text-sm tracking-[0.18em] uppercase transition-all ${p.highlight ? "text-[#0A0A0A] shadow-[0_15px_40px_-10px_rgba(212,175,55,0.6)] hover:shadow-[0_20px_50px_-10px_rgba(212,175,55,0.8)]" : "text-white ring-1 ring-white/25 hover:ring-amber-300/50 hover:text-amber-300"}`} style={p.highlight ? { background: `linear-gradient(135deg, ${CHAMPAGNE} 0%, ${GOLD} 100%)` } : {}} data-testid={`nye-package-cta-${i}`}>
           {p.cta} <ArrowRight className="w-4 h-4 transition-transform group-hover/cta:translate-x-0.5" />
          </a>
         )}

         {p.highlight && (
          <div className="mt-4 flex items-center justify-center gap-2 text-amber-200/80 text-[10px] uppercase tracking-[0.28em] font-bold">
           <span className="relative flex items-center">
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: GOLD }} />
            <span className="absolute inset-0 w-1.5 h-1.5 rounded-full animate-ping" style={{ backgroundColor: GOLD, opacity: 0.7 }} />
           </span>
           Limited tickets · Selling fast
          </div>
         )}
        </div>
       </div>
      ))}
     </div>
    </div>
   </section>

   {/* ═══════════════════════════════════════════════════════════════════════════
    MENU · deep aubergine/plum so it reads "after-dinner lounge"
    ═══════════════════════════════════════════════════════════════════════════ */}
   <section className="relative bg-gradient-to-b from-[#0B0510] via-[#150818] to-[#0B0510] py-24 lg:py-32 overflow-hidden isolate" data-testid="nye-menu">
    <Starfield density={35} color="#FFE9A8" glow="rgba(255,210,120,0.55)" fixed={false} intensity={0.6} />
    <div className="absolute -top-32 right-1/4 w-[34rem] h-[34rem] rounded-full bg-amber-400/[0.08] blur-3xl pointer-events-none" />
    <div className="absolute bottom-0 left-1/3 w-[28rem] h-[28rem] rounded-full bg-fuchsia-500/[0.10] blur-3xl pointer-events-none" />
    <div className="relative z-10 mx-auto max-w-5xl px-5 lg:px-10">
     <div className="text-center max-w-3xl mx-auto mb-12 lg:mb-14">
      <div className="flex justify-center"><GoldEyebrow>The NYE 2026 menu · what you'll eat &amp; drink</GoldEyebrow></div>
      <h2 className="font-black tracking-tight text-white mt-6 leading-[0.92]" style={{ fontSize: "clamp(2rem, 4.5vw, 4rem)", letterSpacing: "-0.025em" }}>
       Eat, drink &amp; <em className="font-light" style={{ color: GOLD }}>celebrate.</em>
      </h2>
      <p className="text-white/65 text-base lg:text-lg leading-[1.75] mt-5 font-light">
       Every Magic NYE ticket is fully inclusive, canapés, buffet, dessert and a premium open bar. Tap each course below to see exactly what&apos;s being served on the night.
      </p>
     </div>
     <div className="space-y-3 lg:space-y-4">
      {MENU_BLOCKS.map((b, i) => <MenuAccordion key={b.id} block={b} i={i} defaultOpen={i === 0} />)}
     </div>
     <div className="mt-10 rounded-2xl bg-white/[0.04] ring-1 ring-white/10 px-5 lg:px-7 py-5 lg:py-6 backdrop-blur flex flex-wrap items-center gap-x-6 gap-y-2 text-[11px] uppercase tracking-[0.24em] font-bold text-white/70">
      <span className="inline-flex items-center gap-2" style={{ color: GOLD }}><Check className="w-3.5 h-3.5" strokeWidth={3} /> All inclusive</span>
      <span className="inline-flex items-center gap-2"><Check className="w-3.5 h-3.5 text-white/40" strokeWidth={3} /> Dietary requests on request</span>
      <span className="inline-flex items-center gap-2"><Check className="w-3.5 h-3.5 text-white/40" strokeWidth={3} /> Vegan & gluten free options</span>
     </div>
    </div>
   </section>



   {/* ═══════════════════════════════════════════════════════════════════════════
    EVENT TIMELINE · midnight navy with starfield (matches the late-night run sheet)
    ═══════════════════════════════════════════════════════════════════════════ */}
   <section className="relative bg-gradient-to-b from-[#070418] via-[#0A0626] to-[#070418] py-24 lg:py-32 overflow-hidden isolate" data-testid="nye-timeline">
    <Starfield density={60} color="#FFE9A8" glow="rgba(255,210,120,0.6)" fixed={false} intensity={0.7} />
    <div className="absolute -top-32 right-1/3 w-[40rem] h-[40rem] rounded-full bg-amber-400/[0.08] blur-3xl pointer-events-none" />
    <div className="absolute -bottom-32 left-1/3 w-[34rem] h-[34rem] rounded-full bg-fuchsia-500/[0.08] blur-3xl pointer-events-none" />
    <div className="absolute inset-0 pointer-events-none opacity-40">
     <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-300/50 to-transparent" />
    </div>
    <div className="relative z-10 mx-auto max-w-7xl px-5 lg:px-10">
     <div className="text-center max-w-3xl mx-auto mb-16">
      <div className="flex justify-center"><GoldEyebrow>The run sheet</GoldEyebrow></div>
      <h2 className="font-black tracking-tight text-white mt-6 leading-[0.92]" style={{ fontSize: "clamp(2rem, 4.5vw, 4rem)", letterSpacing: "-0.025em" }}>
       Your night, <em className="font-light" style={{ color: GOLD }}>hour by hour.</em>
      </h2>
     </div>

     <div className="relative max-w-4xl mx-auto">
      {/* Vertical gold line */}
      <div className="absolute left-[3.25rem] lg:left-1/2 lg:-translate-x-px top-2 bottom-2 w-px" style={{ background: `linear-gradient(180deg, transparent 0%, ${GOLD} 15%, ${GOLD} 85%, transparent 100%)`, opacity: 0.4 }} />

      <ol className="space-y-10 lg:space-y-14">
       {TIMELINE.map((t, i) => {
        const isHighlight = t.title.includes("MIDNIGHT");
        const isRight = i % 2 === 1;
        return (
         <li key={i} className={`relative grid lg:grid-cols-2 gap-6 lg:gap-12 items-center ${isRight ? "lg:[direction:rtl]" : ""}`} data-testid={`nye-timeline-${i}`}>
          <div className={`pl-24 lg:pl-0 ${isRight ? "lg:[direction:ltr] lg:text-right lg:pr-14" : "lg:pl-14"}`}>
           <div className={`rounded-2xl p-6 lg:p-7 backdrop-blur transition-all hover:scale-[1.02] ${isHighlight ? "bg-gradient-to-br from-amber-300/15 via-white/[0.04] to-transparent ring-1 ring-amber-300/40 shadow-[0_30px_70px_-15px_rgba(212,175,55,0.4)]" : "bg-white/[0.04] ring-1 ring-white/10"}`}>
            <div className="text-[11px] uppercase tracking-[0.4em] font-black mb-2" style={{ color: GOLD }}>{t.time}</div>
            <h3 className="font-black text-white text-xl lg:text-2xl tracking-tight leading-tight">{t.title}</h3>
            <p className="text-white/65 text-sm lg:text-base leading-relaxed mt-3 font-light">{t.body}</p>
           </div>
          </div>
          {/* Timeline marker */}
          <div className="absolute top-7 left-9 lg:left-1/2 lg:-translate-x-1/2 z-10">
           <div className={`relative w-6 h-6 rounded-full ring-4 ring-[#0A0A0A] ${isHighlight ? "shadow-[0_0_30px_rgba(212,175,55,0.8)]" : ""}`} style={{ backgroundColor: GOLD }}>
            {isHighlight && <span className="absolute inset-0 rounded-full animate-ping" style={{ backgroundColor: GOLD, opacity: 0.5 }} />}
           </div>
          </div>
          <div className="hidden lg:block" />
         </li>
        );
       })}
      </ol>
     </div>
    </div>
   </section>


   {/* ═══════════════════════════════════════════════════════════════════════════
    SOCIAL PROOF · deep plum gradient with starfield
    ═══════════════════════════════════════════════════════════════════════════ */}
   <section className="relative bg-gradient-to-b from-[#160820] via-[#1A0B26] to-[#0E0618] py-24 lg:py-32 border-y border-amber-300/15 overflow-hidden isolate" data-testid="nye-reviews">
    <Starfield density={40} color="#FFE9A8" glow="rgba(255,210,120,0.55)" fixed={false} intensity={0.55} />
    <div className="absolute -top-32 right-1/3 w-[36rem] h-[36rem] rounded-full bg-amber-400/[0.08] blur-3xl pointer-events-none" />
    <div className="absolute -bottom-32 left-1/4 w-[30rem] h-[30rem] rounded-full bg-fuchsia-500/[0.10] blur-3xl pointer-events-none" />
    <div className="relative z-10 mx-auto max-w-7xl px-5 lg:px-10">
     <div className="text-center max-w-3xl mx-auto mb-14">
      <div className="flex justify-center"><GoldEyebrow>2,500+ five-star reviews</GoldEyebrow></div>
      <h2 className="font-black tracking-tight text-white mt-6 leading-[0.92]" style={{ fontSize: "clamp(2rem, 4.5vw, 4rem)", letterSpacing: "-0.025em" }}>
       What past NYE guests <em className="font-light" style={{ color: GOLD }}>still talk about.</em>
      </h2>
      <div className="flex items-center justify-center gap-1 mt-6">
       {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5" fill={GOLD} strokeWidth={0} />)}
       <span className="ml-3 text-white/65 text-sm font-bold">5.0 average on Google</span>
      </div>
     </div>

     <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
      {REVIEWS.map((r, i) => (
       <div key={i} className="relative bg-[#141416] rounded-2xl p-6 ring-1 ring-white/10 hover:ring-amber-300/30 transition-all hover:-translate-y-1 group" data-testid={`nye-review-${i}`}>
        <div className="flex gap-0.5 mb-3">{[...Array(r.rating)].map((_, j) => <Star key={j} className="w-3.5 h-3.5" fill={GOLD} strokeWidth={0} />)}</div>
        <p className="text-white/85 text-sm leading-relaxed">"{r.body}"</p>
        <div className="mt-5 pt-4 border-t border-white/10 flex items-center gap-3">
         <div className="w-9 h-9 rounded-full flex items-center justify-center font-black text-xs text-[#0A0A0A] shrink-0" style={{ background: `linear-gradient(135deg, ${CHAMPAGNE} 0%, ${GOLD} 100%)` }}>{r.name.split(" ").map(w => w[0]).join("")}</div>
         <div>
          <div className="font-black text-white text-sm">{r.name}</div>
          <div className="text-white/50 text-[10px] uppercase tracking-[0.24em] font-bold mt-0.5">{r.role}</div>
         </div>
        </div>
       </div>
      ))}
     </div>
    </div>
   </section>

   {/* ═══════════════════════════════════════════════════════════════════════════
    FAQ · deep matte black with subtle starfield
    ═══════════════════════════════════════════════════════════════════════════ */}
   <section className="relative bg-gradient-to-b from-[#06060A] via-[#080810] to-[#06060A] py-24 lg:py-32 overflow-hidden isolate" data-testid="nye-faq">
    <Starfield density={25} color="#FFE9A8" glow="rgba(255,210,120,0.4)" fixed={false} intensity={0.45} />
    <div className="relative z-10 mx-auto max-w-4xl px-5 lg:px-10">
     <div className="mb-12">
      <GoldEyebrow>Frequently asked</GoldEyebrow>
      <h2 className="font-black tracking-tight text-white mt-6 leading-[0.92]" style={{ fontSize: "clamp(2rem, 4.5vw, 4rem)", letterSpacing: "-0.025em" }}>
       The questions <em className="font-light" style={{ color: GOLD }}>everyone asks.</em>
      </h2>
     </div>
     <div>{FAQS.map((f, i) => <FaqItem key={i} {...f} i={i} />)}</div>
    </div>
   </section>

   {/* ═══════════════════════════════════════════════════════════════════════════
    FINAL CTA · starfield over the photo for cohesion with the rest of the page
    ═══════════════════════════════════════════════════════════════════════════ */}
   <section id="enquiry" className="relative overflow-hidden isolate scroll-mt-24" data-testid="nye-final-cta">
    <img src={MAGIC_PHOTOS.nyeFireworksHero} alt="Sydney NYE midnight fireworks final CTA" className="absolute inset-0 w-full h-full object-cover scale-110" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#06060A]/95 via-[#0A0626]/75 to-[#0A0626]/45" />
    <Starfield density={55} color="#FFE9A8" glow="rgba(255,210,120,0.6)" fixed={false} intensity={0.7} />
    <div className="absolute -top-32 right-1/4 w-[40rem] h-[40rem] rounded-full bg-amber-400/15 blur-3xl pointer-events-none animate-pulse" style={{ animationDuration: "6s" }} />
    <div className="relative z-10 mx-auto max-w-7xl px-5 lg:px-10 py-24 lg:py-36 grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-16 items-start">
     <div className="max-w-2xl">
      <GoldEyebrow>31 December 2026 · King St Wharf</GoldEyebrow>
      <h2 className="font-black tracking-tight text-white mt-6 leading-[0.88]" style={{ fontSize: "clamp(2.5rem, 6vw, 5.5rem)", letterSpacing: "-0.03em" }}>
       Don't watch NYE.<br /><em className="font-light bg-clip-text text-transparent" style={{ backgroundImage: `linear-gradient(135deg, ${CHAMPAGNE} 0%, ${GOLD} 100%)` }}>Live it.</em>
      </h2>
      <p className="text-white/85 text-lg lg:text-xl leading-snug mt-7 font-light">
       NYE 2026 is selling fast , don't miss your seat to welcome in 2027. Magic phones are open from 9am every day.
      </p>
      <div className="mt-10 flex flex-wrap items-center gap-3">
       <Link to="/cruises/nye-cruises-sydney-harbour/book" className="group inline-flex items-center gap-2.5 px-8 py-4 rounded-full font-black text-sm tracking-[0.18em] uppercase shadow-[0_20px_50px_-10px_rgba(212,175,55,0.6)] hover:scale-[1.03] transition-all text-[#0A0A0A]" style={{ background: `linear-gradient(135deg, ${CHAMPAGNE} 0%, ${GOLD} 100%)` }} data-testid="nye-final-buy">
        <Ticket className="w-4 h-4" /> Buy NYE Tickets
       </Link>
       <a href={`tel:${CONTACT.phone1}`} className="group inline-flex items-center gap-2.5 text-white hover:text-amber-300 px-6 py-4 rounded-full font-black text-sm tracking-[0.18em] uppercase border border-white/30 hover:border-amber-300/60 backdrop-blur transition-all" data-testid="nye-final-call">
        <Phone className="w-4 h-4" /> Call {CONTACT.phone1}
       </a>
      </div>
      <p className="mt-8 text-amber-200/80 text-sm leading-relaxed max-w-md">
       Booking a <strong className="text-amber-200">group of 10+</strong> with deposit-on-the-day terms? Drop your details on the right , Magic will quote within 24 hours.
      </p>
     </div>
     <div className="lg:sticky lg:top-24" data-testid="nye-group-enquiry-form">
      <EnquiryForm defaultEvent="NYE group booking" sourcePage="nye-cruises-sydney-harbour" />
     </div>
    </div>
   </section>

   {/* ═══════════════════════════════════════════════════════════════════════════
    STICKY MOBILE CTA
    ═══════════════════════════════════════════════════════════════════════════ */}
   {showStickyCTA && (
    <div className="fixed bottom-0 left-0 right-0 z-50 lg:hidden bg-[#0A0A0A] border-t border-amber-300/30 px-4 py-3 backdrop-blur-xl animate-in slide-in-from-bottom duration-300" data-testid="nye-sticky-mobile">
     <div className="flex items-center justify-between gap-3">
      <div>
       <div className="text-[9px] uppercase tracking-[0.28em] font-black" style={{ color: GOLD }}>From {adultStr} pp</div>
       <div className="text-white font-black text-sm tracking-tight mt-0.5">NYE 2026 · 31 Dec</div>
      </div>
      <Link to="/cruises/nye-cruises-sydney-harbour/book" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[#0A0A0A] font-black text-xs tracking-[0.18em] uppercase shadow-[0_10px_30px_-5px_rgba(212,175,55,0.6)]" style={{ background: `linear-gradient(135deg, ${CHAMPAGNE} 0%, ${GOLD} 100%)` }} data-testid="nye-sticky-mobile-cta">
       Buy Tickets <ArrowRight className="w-3.5 h-3.5" />
      </Link>
     </div>
    </div>
   )}
  </main>
 );
}
